import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import ScrollProgress from "@/components/site/ScrollProgress";
import MobileStickyCTA from "@/components/site/MobileStickyCTA";
import { FadeUp } from "@/components/site/_shared";
import useDocumentMeta from "@/hooks/useDocumentMeta";
import { EVIDENCE_PAGES } from "@/data/evidencePages";
import { GradeBadge } from "./EvidencePage";
import { organizationSchema, breadcrumbSchema, evidenceOgImage, SITE_URL} from "@/lib/seo";

export default function EvidenceLibrary() {
  useDocumentMeta({
    title: "Evidence Library — Physician-Reviewed Science | Integrated Wellness",
    description:
      "The Integrated Wellness Evidence Library: physician-reviewed summaries of the science behind biological age, insulin resistance, hormone therapy, the microbiome and wearable data — with graded, peer-reviewed citations.",
    image: evidenceOgImage("index"),
    url: `${SITE_URL}/evidence`,
    schema: [
      organizationSchema(),
      breadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Evidence Library", url: "/evidence" },
      ]),
    ],
  });

  const pages = Object.values(EVIDENCE_PAGES);

  return (
    <div data-testid="evidence-library-page" className="min-h-screen bg-background">
      <ScrollProgress />
      <Header />

      <main className="pt-28 md:pt-32 pb-20 md:pb-28">
        <section className="relative max-w-5xl mx-auto px-4 md:px-8">
          <div className="absolute inset-0 iw-grid-bg opacity-30 -z-10" aria-hidden="true" />
          <div className="iw-overline">Integrated Wellness Evidence Library</div>
          <h1 className="mt-4 font-display font-bold tracking-tight leading-[1.05] text-[clamp(2rem,4.8vw,3.5rem)] text-primary">
            Every claim, <span className="text-accent">graded and cited</span>.
          </h1>
          <p className="mt-6 max-w-2xl text-lg md:text-xl text-foreground/80 leading-relaxed">
            Physician-reviewed summaries of the science behind our clinical concepts — what is
            established, what is emerging, and where the limits are. Every page carries
            peer-reviewed references and an explicit evidence grade.
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            {["A", "B", "C", "Emerging"].map((g) => (
              <GradeBadge key={g} grade={g} />
            ))}
          </div>
        </section>

        <section className="max-w-5xl mx-auto px-4 md:px-8 mt-14">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {pages.map((p, i) => (
              <FadeUp key={p.slug} delay={i * 0.05}>
                <a
                  href={`/evidence/${p.slug}`}
                  data-testid={`evidence-card-${p.slug}`}
                  className="iw-track-card group flex flex-col h-full p-7 md:p-8"
                >
                  <div className="flex items-center justify-between gap-3">
                    <span className="font-mono text-[0.6rem] uppercase tracking-[0.2em] text-muted-foreground">
                      {p.eyebrow}
                    </span>
                    <GradeBadge grade={p.grade} small />
                  </div>
                  <h2 className="mt-4 font-display text-lg md:text-xl font-bold leading-tight text-primary">
                    {p.h1}
                  </h2>
                  <p className="mt-3 text-sm md:text-base text-foreground/70 leading-relaxed flex-1 line-clamp-4">
                    {p.directAnswer}
                  </p>
                  <span className="mt-5 inline-flex items-center gap-2 font-mono text-[0.62rem] uppercase tracking-[0.2em] text-accent font-semibold group-hover:gap-3 transition-all">
                    Read the evidence <span aria-hidden>→</span>
                  </span>
                </a>
              </FadeUp>
            ))}
          </div>

          <FadeUp className="mt-14 text-center">
            <a
              href="/get-started"
              data-testid="evidence-library-cta"
              className="iw-btn-red inline-flex items-center gap-2 px-8 py-4 text-[0.72rem]"
            >
              Start My Health Assessment <span aria-hidden>→</span>
            </a>
          </FadeUp>
        </section>
      </main>

      <Footer />
      <MobileStickyCTA />
    </div>
  );
}
